import type { BasemapRasterRules } from "./manifest";
import { transform } from "./tiles";

/**
 * Writes and reads PMTiles v3 archives: a 127-byte header, a gzipped root directory, gzipped JSON
 * metadata, leaf directories when the root would pass its 16 KiB, and the tiles in tile id order.
 * The writer stores identical tiles once.
 */

export const PMTILES_TILE_TYPE = { webp: 4, avif: 5 } as const;
export type PmtilesTileType = keyof typeof PMTILES_TILE_TYPE;
/**
 * A feature set's tiles are PMTiles tile type 0, which PMTiles leaves to the file to say, stored
 * gzipped: each is a `BasemapFeatureTile`'s JSON.
 */
export const PMTILES_FEATURE_TILE_TYPE = 0;

/**
 * The PMTiles tile type a raster's tiles are encoded as: its declared codec for color, lossless
 * WebP for everything else.
 */
export const tileTypeOf = (rules: BasemapRasterRules): PmtilesTileType =>
  rules.type === "color" && rules.encoding.codec === "avif" ? "avif" : "webp";

export interface PmtilesTile {
  z: number;
  x: number;
  y: number;
  bytes: Uint8Array;
}

interface Entry {
  tileId: number;
  offset: number;
  length: number;
  runLength: number;
}

const HEADER_BYTES = 127;
const FIRST_READ_BYTES = 16_384;
const ROOT_BYTES = FIRST_READ_BYTES - HEADER_BYTES;
const GZIP = 2;
const NO_COMPRESSION = 1;

/**
 * An archive of `tiles`, each already encoded: images as they are, or a feature set's tiles
 * gzipped, which its header says so readers unzip them.
 */
export async function writePmtiles(
  tiles: readonly PmtilesTile[],
  tileType: PmtilesTileType | "features",
  metadata: Record<string, unknown>,
): Promise<Uint8Array> {
  const sorted = tiles
    .map(tile => ({ ...tile, tileId: zxyToTileId(tile.z, tile.x, tile.y) }))
    .sort((a, b) => a.tileId - b.tileId);

  const offsets = new Map<string, number>();
  const chunks: Uint8Array[] = [];
  const entries: Entry[] = [];
  let dataLength = 0;
  for (const tile of sorted) {
    const key = await digest(tile.bytes);
    let offset = offsets.get(key);
    if (offset === undefined) {
      offset = dataLength;
      offsets.set(key, offset);
      chunks.push(tile.bytes);
      dataLength += tile.bytes.byteLength;
    }
    const last = entries.at(-1);
    if (last && last.offset === offset && last.tileId + last.runLength === tile.tileId) {
      last.runLength++;
    } else {
      entries.push({ tileId: tile.tileId, offset, length: tile.bytes.byteLength, runLength: 1 });
    }
  }

  const { root, leaves } = await directories(entries);
  const metadataBytes = await gzip(new TextEncoder().encode(JSON.stringify(metadata)));

  const rootOffset = HEADER_BYTES;
  const metadataOffset = rootOffset + root.byteLength;
  const leavesOffset = metadataOffset + metadataBytes.byteLength;
  const dataOffset = leavesOffset + leaves.byteLength;

  const header = new DataView(new ArrayBuffer(HEADER_BYTES));
  new TextEncoder().encodeInto("PMTiles", new Uint8Array(header.buffer));
  header.setUint8(7, 3);
  const u64 = (at: number, value: number) => header.setBigUint64(at, BigInt(value), true);
  u64(8, rootOffset);
  u64(16, root.byteLength);
  u64(24, metadataOffset);
  u64(32, metadataBytes.byteLength);
  u64(40, leavesOffset);
  u64(48, leaves.byteLength);
  u64(56, dataOffset);
  u64(64, dataLength);
  u64(72, sorted.length);
  u64(80, entries.length);
  u64(88, chunks.length);
  header.setUint8(96, 1);
  header.setUint8(97, GZIP);
  const features = tileType === "features";
  header.setUint8(98, features ? GZIP : NO_COMPRESSION);
  header.setUint8(99, features ? PMTILES_FEATURE_TILE_TYPE : PMTILES_TILE_TYPE[tileType]);
  header.setUint8(100, sorted[0]?.z ?? 0);
  header.setUint8(101, sorted.at(-1)?.z ?? 0);
  header.setInt32(102, -180e7, true);
  header.setInt32(106, -90e7, true);
  header.setInt32(110, 180e7, true);
  header.setInt32(114, 90e7, true);

  const archive = new Uint8Array(dataOffset + dataLength);
  archive.set(new Uint8Array(header.buffer), 0);
  archive.set(root, rootOffset);
  archive.set(metadataBytes, metadataOffset);
  archive.set(leaves, leavesOffset);
  let at = dataOffset;
  for (const chunk of chunks) {
    archive.set(chunk, at);
    at += chunk.byteLength;
  }
  return archive;
}

/** The root directory, and leaf directories once the root alone would be too large. */
async function directories(entries: Entry[]) {
  const root = await gzip(serialize(entries));
  if (root.byteLength <= ROOT_BYTES) return { root, leaves: new Uint8Array() };
  for (let perLeaf = 4096; ; perLeaf *= 2) {
    const leafBytes: Uint8Array[] = [];
    const rootEntries: Entry[] = [];
    let offset = 0;
    for (let start = 0; start < entries.length; start += perLeaf) {
      const leaf = await gzip(serialize(entries.slice(start, start + perLeaf)));
      rootEntries.push({
        tileId: entries[start]?.tileId ?? 0,
        offset,
        length: leaf.byteLength,
        runLength: 0,
      });
      leafBytes.push(leaf);
      offset += leaf.byteLength;
    }
    const nextRoot = await gzip(serialize(rootEntries));
    if (nextRoot.byteLength <= ROOT_BYTES) {
      const leaves = new Uint8Array(offset);
      let at = 0;
      for (const leaf of leafBytes) {
        leaves.set(leaf, at);
        at += leaf.byteLength;
      }
      return { root: nextRoot, leaves };
    }
  }
}

function serialize(entries: Entry[]): Uint8Array {
  const bytes: number[] = [];
  const varint = (value: number) => {
    let rest = value;
    while (rest >= 0x80) {
      bytes.push((rest % 0x80) | 0x80);
      rest = Math.floor(rest / 0x80);
    }
    bytes.push(rest);
  };
  varint(entries.length);
  let lastId = 0;
  for (const entry of entries) {
    varint(entry.tileId - lastId);
    lastId = entry.tileId;
  }
  for (const entry of entries) varint(entry.runLength);
  for (const entry of entries) varint(entry.length);
  entries.forEach((entry, index) => {
    const previous = entries[index - 1];
    if (previous && entry.offset === previous.offset + previous.length) varint(0);
    else varint(entry.offset + 1);
  });
  return new Uint8Array(bytes);
}

const gzip = (bytes: Uint8Array) => transform(bytes, new CompressionStream("gzip"));
const gunzip = (bytes: Uint8Array) => transform(bytes, new DecompressionStream("gzip"));

async function digest(bytes: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", bytes as Uint8Array<ArrayBuffer>);
  return sha256Hex(new Uint8Array(hash));
}

export const sha256Hex = (bytes: Uint8Array) =>
  Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");

export async function sha256(bytes: Uint8Array): Promise<string> {
  return digest(bytes);
}

/** A tile's place on PMTiles' Hilbert curve, counted across every coarser zoom. */
export function zxyToTileId(z: number, x: number, y: number): number {
  let acc = 0;
  for (let zoom = 0; zoom < z; zoom++) acc += 4 ** zoom;
  const n = 2 ** z;
  let tx = x;
  let ty = y;
  let d = 0;
  for (let s = n / 2; s >= 1; s /= 2) {
    const rx = (tx & s) > 0 ? 1 : 0;
    const ry = (ty & s) > 0 ? 1 : 0;
    d += s * s * ((3 * rx) ^ ry);
    if (ry === 0) {
      if (rx === 1) {
        tx = s - 1 - tx;
        ty = s - 1 - ty;
      }
      [tx, ty] = [ty, tx];
    }
  }
  return acc + d;
}

/** A tile's zoom, column, and row from its place on the Hilbert curve. */
export function tileIdToZxy(tileId: number): { z: number; x: number; y: number } {
  let acc = 0;
  for (let z = 0; z < 32; z++) {
    const count = 4 ** z;
    if (tileId < acc + count) {
      let d = tileId - acc;
      let x = 0;
      let y = 0;
      for (let s = 1; s < 2 ** z; s *= 2) {
        const rx = 1 & Math.floor(d / 2);
        const ry = 1 & (d ^ rx);
        if (ry === 0) {
          if (rx === 1) {
            x = s - 1 - x;
            y = s - 1 - y;
          }
          [x, y] = [y, x];
        }
        x += s * rx;
        y += s * ry;
        d = Math.floor(d / 4);
      }
      return { z, x, y };
    }
    acc += count;
  }
  throw new Error(`Tile id ${tileId} is past zoom 31.`);
}

/** Bytes that can be read at any offset: a file on disk, or an object read by ranges. */
export interface ByteSource {
  read(offset: number, length: number): Promise<Uint8Array>;
}

export interface PmtilesHeader {
  rootDirectoryOffset: number;
  rootDirectoryLength: number;
  metadataOffset: number;
  metadataLength: number;
  leafDirectoriesOffset: number;
  leafDirectoriesLength: number;
  tileDataOffset: number;
  tileDataLength: number;
  addressedTiles: number;
  tileEntries: number;
  tileContents: number;
  clustered: boolean;
  internalCompression: number;
  tileCompression: number;
  tileType: number;
  minZoom: number;
  maxZoom: number;
}

/**
 * One directory entry for tiles: `runLength` tiles from `tileId` on, all the same bytes at `offset`
 * in the tile data.
 */
export interface PmtilesEntry {
  tileId: number;
  offset: number;
  length: number;
  runLength: number;
}

/** Reads a PMTiles v3 archive through a byte source, fetching only the parts it needs. */
export class PmtilesReader {
  private constructor(
    readonly source: ByteSource,
    readonly header: PmtilesHeader,
    private readonly root: PmtilesEntry[],
  ) {}

  /** Reads the header and root directory, which a writer keeps inside the first 16 KiB. */
  static async open(source: ByteSource): Promise<PmtilesReader> {
    const first = await source.read(0, FIRST_READ_BYTES);
    if (first.byteLength < HEADER_BYTES) throw new Error("It's too short to be PMTiles.");
    if (new TextDecoder().decode(first.subarray(0, 7)) !== "PMTiles") {
      throw new Error("It doesn't start with PMTiles' magic bytes.");
    }
    const view = new DataView(first.buffer, first.byteOffset, first.byteLength);
    const version = view.getUint8(7);
    if (version !== 3) throw new Error(`It's PMTiles version ${version}, not 3.`);
    const u64 = (at: number) => Number(view.getBigUint64(at, true));
    const header: PmtilesHeader = {
      rootDirectoryOffset: u64(8),
      rootDirectoryLength: u64(16),
      metadataOffset: u64(24),
      metadataLength: u64(32),
      leafDirectoriesOffset: u64(40),
      leafDirectoriesLength: u64(48),
      tileDataOffset: u64(56),
      tileDataLength: u64(64),
      addressedTiles: u64(72),
      tileEntries: u64(80),
      tileContents: u64(88),
      clustered: view.getUint8(96) === 1,
      internalCompression: view.getUint8(97),
      tileCompression: view.getUint8(98),
      tileType: view.getUint8(99),
      minZoom: view.getUint8(100),
      maxZoom: view.getUint8(101),
    };
    const { rootDirectoryOffset: offset, rootDirectoryLength: length } = header;
    const rootBytes =
      offset + length <= first.byteLength
        ? first.subarray(offset, offset + length)
        : await source.read(offset, length);
    const root = deserialize(await decompress(rootBytes, header.internalCompression));
    return new PmtilesReader(source, header, root);
  }

  async metadata(): Promise<unknown> {
    const { metadataOffset, metadataLength, internalCompression } = this.header;
    if (metadataLength === 0) return {};
    const bytes = await this.source.read(metadataOffset, metadataLength);
    return JSON.parse(new TextDecoder().decode(await decompress(bytes, internalCompression)));
  }

  /** Every tile entry in tile id order, through every leaf directory. */
  async *entries(): AsyncGenerator<PmtilesEntry> {
    yield* this.#walk(this.root, 0);
  }

  async *#walk(directory: PmtilesEntry[], depth: number): AsyncGenerator<PmtilesEntry> {
    for (const entry of directory) {
      if (entry.runLength > 0) {
        yield entry;
        continue;
      }
      if (depth >= 3) throw new Error("Its leaf directories nest deeper than PMTiles allows.");
      const bytes = await this.source.read(
        this.header.leafDirectoriesOffset + entry.offset,
        entry.length,
      );
      const leaf = deserialize(await decompress(bytes, this.header.internalCompression));
      yield* this.#walk(leaf, depth + 1);
    }
  }

  /** An entry's tile bytes. */
  async tileBytes(entry: PmtilesEntry): Promise<Uint8Array> {
    return this.source.read(this.header.tileDataOffset + entry.offset, entry.length);
  }

  /** One tile's bytes, or null where the archive has none. */
  async getTile(z: number, x: number, y: number): Promise<Uint8Array | null> {
    const tileId = zxyToTileId(z, x, y);
    let directory = this.root;
    for (let depth = 0; depth <= 3; depth++) {
      const entry = findEntry(directory, tileId);
      if (!entry) return null;
      if (entry.runLength > 0) return this.tileBytes(entry);
      const bytes = await this.source.read(
        this.header.leafDirectoriesOffset + entry.offset,
        entry.length,
      );
      directory = deserialize(await decompress(bytes, this.header.internalCompression));
    }
    return null;
  }
}

/** The entry holding a tile id: the last one at or before it, if its run or leaf reaches it. */
function findEntry(directory: PmtilesEntry[], tileId: number): PmtilesEntry | null {
  let low = 0;
  let high = directory.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const entry = directory[middle] as PmtilesEntry;
    if (entry.tileId === tileId) return entry;
    if (entry.tileId < tileId) low = middle + 1;
    else high = middle - 1;
  }
  const before = directory[high];
  if (!before) return null;
  if (before.runLength === 0) return before;
  return tileId - before.tileId < before.runLength ? before : null;
}

async function decompress(bytes: Uint8Array, compression: number): Promise<Uint8Array> {
  if (compression === GZIP) return gunzip(bytes);
  if (compression === NO_COMPRESSION) return bytes;
  throw new Error(`Its internal compression is ${compression}; Pax reads gzip (2) or none (1).`);
}

function deserialize(bytes: Uint8Array): PmtilesEntry[] {
  let at = 0;
  const varint = () => {
    let value = 0;
    let scale = 1;
    for (;;) {
      const byte = bytes[at++];
      if (byte === undefined) throw new Error("A directory ends in the middle of a number.");
      value += (byte & 0x7f) * scale;
      if (byte < 0x80) return value;
      scale *= 0x80;
    }
  };
  const count = varint();
  const entries: PmtilesEntry[] = Array.from({ length: count }, () => ({
    tileId: 0,
    offset: 0,
    length: 0,
    runLength: 0,
  }));
  let lastId = 0;
  for (const entry of entries) {
    lastId += varint();
    entry.tileId = lastId;
  }
  for (const entry of entries) entry.runLength = varint();
  for (const entry of entries) entry.length = varint();
  entries.forEach((entry, index) => {
    const offset = varint();
    const previous = entries[index - 1];
    if (offset === 0 && previous) entry.offset = previous.offset + previous.length;
    else entry.offset = offset - 1;
  });
  return entries;
}
